import React, { useState, useEffect, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Grid,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  CircularProgress,
  Dialog,
  DialogContent,
  TextField,
  MenuItem,
  capitalize,
  DialogTitle,
  DialogContentText,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from "@material-ui/core";
import { Undo } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import CloseIcon from "@material-ui/icons/Close";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";

import { BaseUrl, ImageBaseUrl } from "../../../Environment";
import { getSecureItem } from "../../../utilsFunctions";
import { Phone } from "@material-ui/icons";
import { PreCloseSaving } from "../../modals/PreCloseLoan/PreCloseSaving";

const TOAST_OPTS = {
  position: "top-center",
  autoClose: 3000,
  hideProgressBar: true,
  closeOnClick: false,
  pauseOnHover: false,
  draggable: false,
};

const PAYMENT_MODES = ["Cash", "Online", "Cheque"];

const TENURE_LABELS = {
  daily: 'Days',
  ten_daily: 'Ten-Days',
  weekly: 'Weeks',
  bi_weekly: 'Bi-Weeks',
};

// Single source of truth for colors + font so nothing drifts out of sync.
const COLOR_PAID = "#00d95e";
const COLOR_NOT_PAID = "#c10707";
const COLOR_ACCENT = "#4d66f0";
const FONT_FAMILY = '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif';

// ---------------- Schedule edit mode ----------------
// Statuses the schedule editor can write. "" clears the row back to pending.
const STATUS_OPTIONS = [
  { value: "", label: "Pending" },
  { value: "PAID", label: "Paid" },
  { value: "NOT_PAID", label: "Not Paid" },
];

// Editable columns and how they are rendered. Keys match the API allow-list in
// editSavingCollection, so the payload can be sent through unchanged.
const NUMERIC_FIELDS = [
  "openingBalance",
  "collectionAmount",
  "closingBalance",
  "interest",
  "maturityAmount",
  "penaltyApplied",
];
const DATE_FIELDS = ["dueDate", "collectedDate"];

// Warnings shown once per edit session — the API trusts the numbers it is
// given and does not recompute later rows, so the operator has to.
const FIELD_WARNINGS = {
  collectionAmount:
    "Update the opening & closing balances on this row, and the next row's opening balance.",
  penaltyApplied:
    "After adding a penalty, include the penalty amount in the next row's collection amount so it actually gets collected.",
  interest:
    "Interest is not recalculated for you — update the maturity amount and later rows to match.",
  maturityAmount:
    "Maturity amount is not recalculated for you — check the later rows so the schedule stays consistent.",
};

const useStyles = makeStyles((theme) => ({
  // Applying fontFamily once here and cascading with '& *' means every
  // descendant (including raw <text> inside the PercentRing SVG) inherits
  // the same font automatically — no need to tag each element manually.
  page: {
    minHeight: "100vh",
    background: "#f5f6fa",
    fontFamily: FONT_FAMILY,
    "& *": {
      fontFamily: "inherit",
    },
  },

  // ---------------- Left blue sidebar ----------------
  sidebar: {
    background: "#4d66f0",
    color: "#fff",
    padding: theme.spacing(2),
    minHeight: "100%",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
    },
  },
  sidebarTopRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(4),
  },
  backIcon: {
    color: "#fff",
  },
  avatarWrapper: {
    textAlign: "center",
    marginBottom: theme.spacing(2),
  },
  avatarIcon: {
    fontSize: 80,
    color: "#fff",
  },
  customerName: {
    textAlign: "center",
    fontWeight: 600,
    fontSize: "1.2rem",
    marginBottom: theme.spacing(1),
    wordBreak: "break-word",
  },
  phoneRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(4),
  },
  nextPaymentLabel: {
    fontSize: "0.8rem",
    fontWeight: 300,
    letterSpacing: 1,
    color: "rgba(255,255,255,0.8)",
    marginBottom: theme.spacing(1),
  },
  nextPaymentDate: {
    fontSize: "0.8rem",
    fontWeight: 300,
    marginBottom: theme.spacing(1),
  },
  nextPaymentAmount: {
    fontSize: "1.3rem",
    fontWeight: 500,
    lineHeight: 1.1,
    marginBottom: theme.spacing(1),
  },
  nextPaymentSub: {
    fontSize: "0.80rem",
    color: "rgba(255,255,255,0.8)",
    marginBottom: theme.spacing(2),
  },
  actionButtonsRow: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  receiveButton: {
    background: COLOR_PAID,
    color: "#fff",
    textTransform: "none",
    fontSize: 12,
    padding: theme.spacing(1, 2),
    minWidth: 120,
    "&:hover": { background: "#00b84f" },
    "&:disabled": {
      background: "rgba(255,255,255,0.3)",
      color: "rgba(255,255,255,0.6)",
    },
  },
  notPaidButton: {
    background: COLOR_NOT_PAID,
    color: "#fff",
    textTransform: "none",
    fontSize: 12,
    padding: theme.spacing(1, 3),
    minWidth: 120,
    "&:hover": { background: "#9c0505" },
    "&:disabled": {
      background: "rgba(255,255,255,0.3)",
      color: "rgba(255,255,255,0.6)",
    },
  },
  transactionHistory: {
    fontSize: "0.9rem",
    fontWeight: 300,
    letterSpacing: 1,
    color: "rgba(255,255,255,0.85)",
    cursor: "pointer",
    textTransform: "uppercase",
    marginTop: "10px",
  },

  // ---------------- Right main panel ----------------
  main: {
    padding: theme.spacing(3, 4),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  infoBar: {
    background: "#fff",
    borderRadius: 12,
    padding: theme.spacing(2.5, 3),
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    marginBottom: theme.spacing(3),
  },
  infoValue: {
    fontWeight: 700,
    color: "#26314f",
    fontSize: "17px",
    wordBreak: "break-word",
  },
  infoLabel: {
    color: "#9aa1b1",
    fontSize: "0.8rem",
  },
  percentRingWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  percentLabel: {
    fontSize: "0.8rem",
    color: "#7a8199",
    marginTop: theme.spacing(0.5),
  },
  statsRow: {
    marginBottom: theme.spacing(3),
  },
  statBox: {
    background: "#4d66f0",
    color: "#fff",
    borderRadius: 12,
    padding: theme.spacing(2.5),
    height: "100%",
  },
  statValue: {
    fontSize: "1.6rem",
    fontWeight: 700,
    wordBreak: "break-word",
  },
  statLabel: {
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.85)",
  },

  scheduleHeaderRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  scheduleTitle: {
    fontWeight: 700,
    fontSize: "1.3rem",
    color: "#26314f",
  },
  scheduleCount: {
    color: "#9aa1b1",
  },
  // Pushes the edit / save controls to the far right of the schedule header.
  scheduleActions: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  editSaveButton: {
    background: COLOR_PAID,
    color: "#fff",
    textTransform: "none",
    fontSize: 12,
    padding: theme.spacing(0.75, 3),
    "&:hover": { background: "#00b84f" },
    "&:disabled": { background: "#b9e8cd", color: "#fff" },
  },
  editCancelButton: {
    background: COLOR_NOT_PAID,
    color: "#fff",
    textTransform: "none",
    fontSize: 12,
    padding: theme.spacing(0.75, 3),
    "&:hover": { background: "#9c0505" },
    "&:disabled": { background: "#e8b9b9", color: "#fff" },
  },
  editIcon: {
    color: "#4d66f0",
  },
  editInput: {
    width: 130,
    background: "#fff",
  },
  tableWrapper: {
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    overflowX: "auto",
  },
  tableHeadCell: {
    fontWeight: 700,
    color: "#26314f",
    borderBottom: "2px solid #eef0f5",
    whiteSpace: "nowrap",
  },
  loaderWrapper: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(8),
  },

  // ---------------- Receive Payment / Not Paid modal ----------------
  modalPaper: {
    borderRadius: 12,
    minWidth: 320,
    width: 560,
    maxWidth: "90vw",
    minHeight: 300,
  },
  modalHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: theme.spacing(3, 3, 2),
  },
  modalDueDate: {
    color: "#9aa1b1",
    fontSize: "0.9rem",
  },
  modalTitlePaid: {
    fontWeight: 600,
    fontSize: "1.4rem",
    // color: COLOR_PAID,
  },
  modalTitleNotPaid: {
    fontWeight: 600,
    fontSize: "1.4rem",
    // color: COLOR_NOT_PAID,
  },
  modalFieldsRow: {
    padding: theme.spacing(0, 3),
  },
  modalFieldLabel: {
    fontSize: "0.85rem",
    color: "#7a8199",
    marginBottom: theme.spacing(0.5),
  },
  modalFooter: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(3),
  },
  modalTotalAmount: {
    fontWeight: 700,
    fontSize: "1.6rem",
    color: "#26314f",
  },
  modalTotalLabel: {
    fontSize: "0.8rem",
    color: "#9aa1b1",
  },
  modalCancelButton: {
    textTransform: "none",
    marginRight: theme.spacing(2),
  },
  modalConfirmButtonPaid: {
    background: COLOR_PAID,
    color: "#fff",
    textTransform: "none",
    padding: theme.spacing(0.5, 3),
    "&:hover": { background: "#00b84f" },
  },
  modalConfirmButtonNotPaid: {
    background: COLOR_NOT_PAID,
    color: "#fff",
    textTransform: "none",
    padding: theme.spacing(0.5, 3),
    "&:hover": { background: "#9c0505" },
  },

  // Radio buttons (Full / Partial / Prepayment) always use the accent blue,
  // in both the unchecked and checked states.
  radioRoot: {
    color: COLOR_ACCENT,
    "&$radioChecked": {
      color: COLOR_ACCENT,
    },
  },
  radioChecked: {},

  // ---------------- Transaction History ----------------
  transactionHistoryContainer: {
    width: "100%",
    marginTop: theme.spacing(1.5),
  },
  transactionRow: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1.75),
    boxSizing: "border-box",
  },
  dateBox: {
    width: 36,
    minWidth: 36,
    height: 36,
    borderRadius: 5,
    backgroundColor: "#2946C7",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  dateMonth: {
    color: "#fff",
    fontSize: 11,
    lineHeight: "13px",
  },
  dateDay: {
    color: "#fff",
    fontSize: 11,
    lineHeight: "13px",
  },
  amountSection: {
    flex: 1,
    minWidth: 0,
    paddingLeft: theme.spacing(1),
  },
  amount: {
    color: "#fff",
    fontSize: 16,
    lineHeight: "18px",
  },
  installment: {
    color: "rgba(255, 255, 255, 0.65)",
    fontSize: 10,
    lineHeight: "14px",
    whiteSpace: "nowrap",
  },
  statusSection: {
    minWidth: 60,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  statusRow: {
    display: "flex",
    alignItems: "center",
  },
  statusDot: {
    width: 5,
    height: 5,
    minWidth: 5,
    borderRadius: "50%",
    marginRight: 3,
  },
  statusText: {
    fontSize: 10,
    fontWeight: 600,
    lineHeight: "12px",
  },
  paymentRow: {
    display: "flex",
    alignItems: "center",
    marginTop: 3,
  },
  paymentMode: {
    color: COLOR_PAID,
    fontSize: 10,
    fontWeight: 500,
  },
  checkIcon: {
    width: 12,
    height: 12,
    marginLeft: 2,
    borderRadius: "50%",
    backgroundColor: COLOR_PAID,
    color: "#fff",
    fontSize: 8,
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  crossIcon: {
    width: 12,
    height: 12,
    marginLeft: 2,
    borderRadius: "50%",
    backgroundColor: COLOR_NOT_PAID,
    color: "#fff",
    fontSize: 8,
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const formatDate = (isoStr) => {
  if (!isoStr) return "-";
  return new Date(isoStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDDMMYYYY = (isoStr) => {
  if (!isoStr) return "-";
  const d = new Date(isoStr);
  return `${String(d.getDate()).padStart(2, "0")}/${String(
    d.getMonth() + 1,
  ).padStart(2, "0")}/${d.getFullYear()}`;
};

const todayISODate = () => new Date().toISOString().slice(0, 10);

/** YYYY-MM-DD for <input type="date">, without dragging in a date library. */
const toInputDate = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
};

/**
 * Status casing is not consistent in the DB (putSavingCollection writes
 * PAID / NOT_PAID, preCloseSaving writes "Paid"), so never compare with ===.
 */
const normalizeStatus = (status) => {
  if (status === null || status === undefined || status === "") return "";
  const key = String(status).trim().toUpperCase().replace(/[\s-]+/g, "_");
  if (key === "PAID") return "PAID";
  if (key === "NOT_PAID" || key === "NOTPAID" || key === "UNPAID") return "NOT_PAID";
  return key;
};

const isPaidStatus = (status) => normalizeStatus(status) === "PAID";
const isNotPaidStatus = (status) => normalizeStatus(status) === "NOT_PAID";
const isPendingStatus = (status) => normalizeStatus(status) === "";

/**
 * Did the cell actually change? Amounts come back from Sequelize as DECIMAL
 * strings ("1000.00") while the input yields "1000", so numeric columns are
 * compared as numbers — otherwise every touched row would look dirty.
 */
const isSameValue = (field, before, value) => {
  if (NUMERIC_FIELDS.includes(field)) {
    const blank = (v) => v === "" || v === null || v === undefined;
    if (blank(before) || blank(value)) return blank(before) && blank(value);
    const a = Number(before);
    const b = Number(value);
    if (Number.isFinite(a) && Number.isFinite(b)) return a === b;
  }
  if (field === "collectionStatus") {
    return normalizeStatus(before) === normalizeStatus(value);
  }
  return String(before ?? "") === String(value ?? "");
};

/** A saving stays editable until it is closed / cancelled / pre-closed. */
const CLOSED_SAVING_STATUSES = ["CLOSED", "CANCELLED", "CANCELED", "PRECLOSED", "PRE_CLOSED"];
const isSavingEditable = (saving) =>
  !CLOSED_SAVING_STATUSES.includes(
    String(saving?.savingStatus || "").trim().toUpperCase().replace(/[\s-]+/g, "_"),
  );

/** Circular "% Received" ring, driven by actual amount collected so far. */
const PercentRing = ({ percent = 0 }) => {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.min(100, Math.max(0, percent));
  const offset = circumference - (clamped / 100) * circumference;

  return (
    <svg width="100" height="100" viewBox="0 0 100 100">
      <circle
        cx="50"
        cy="50"
        r={radius}
        fill="none"
        stroke="#e6e9f2"
        strokeWidth="7"
      />
      <circle
        cx="50"
        cy="50"
        r={radius}
        fill="none"
        stroke="#6C63FF"
        strokeWidth="7"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform="rotate(-90 50 50)"
        style={{ transition: "stroke-dashoffset 0.4s ease" }}
      />
      <text
        x="50"
        y="55"
        textAnchor="middle"
        fontSize="18"
        fontWeight="700"
        fill="#26314f"
      >
        {clamped}%
      </text>
    </svg>
  );
};

const currency = getSecureItem("country")?.symbol

const SavingDetail = () => {
  const classes = useStyles();
  const history = useHistory();
  const { savingId } = useParams();

  const [saving, setSaving] = useState(null);
  const [savingsCollections, setSavingsCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  // ---- Receive Payment / Not Paid modal state ----
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState("PAID"); // "PAID" | "NOT_PAID"
  const [paymentType, setPaymentType] = useState("FULL");
  const [paymentMode, setPaymentMode] = useState("Cash");
  const [collectedDate, setCollectedDate] = useState(todayISODate());
  const [collectionAmount, setCollectionAmount] = useState(0);
  const [penaltyAmount, setPenaltyAmount] = useState(0);
  const [amountError, setAmountError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [savingToCancel, setSavingToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [reasonError, setReasonError] = useState(false);
  const [showPreClose, setShowPreClose] = useState(false);

  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [rowToRevert, setRowToRevert] = useState(null);
  const [reverting, setReverting] = useState(false);

  // ---- Savings schedule edit mode ----
  // `changes` is keyed by savingsCollectionId → { field: value }, so the PUT
  // payload only ever carries rows and columns the operator actually touched.
  const [isEditing, setIsEditing] = useState(false);
  const [changes, setChanges] = useState({});
  const [savingEdits, setSavingEdits] = useState(false);
  const [warnMessage, setWarnMessage] = useState("");
  const [warnedFields, setWarnedFields] = useState({});

  const userPermissions = getSecureItem("UsersAccess");
  // Falls back to the loan permission so existing roles keep working if the
  // backend has no dedicated `editSaving` flag yet.
  const canEditSchedule = userPermissions?.editSaving ?? userPermissions?.editLoan;

  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${getSecureItem("token")}`,
  });

  const cancelSaving = async (savingId, reason) => {
    return axios.patch(
      `${BaseUrl}/savings/cancelSaving/${savingId}`,
      { reason },
      { headers: authHeaders() }
    );
  };

  const openCancelDialog = (e, saving) => {
    e.stopPropagation();
    setSavingToCancel(saving);
    setCancelReason("");
    setReasonError(false);
    setCancelDialogOpen(true);
  };

  const closeCancelDialog = () => {
    if (cancelling) return; // prevent closing mid-request
    setCancelDialogOpen(false);
    setSavingToCancel(null);
    setCancelReason("");
    setReasonError(false);
  };

  const handleConfirmCancel = async () => {
    if (!cancelReason.trim()) {
      setReasonError(true);
      return;
    }

    setCancelling(true);
    try {
      await cancelSaving(savingToCancel.savingId, cancelReason.trim());
      toast.success("Saving cancelled successfully!", TOAST_OPTS);
      closeCancelDialog();
      history.goBack();
    } catch (err) {
      console.error("Cancel Saving Error:", err);
      toast.error(
        err?.response?.data?.message || "Unable to cancel saving!",
        TOAST_OPTS
      );
    } finally {
      setCancelling(false);
    }
  };

const getRevertableCollection = (list) => {
  let candidate = null;

  for (let i = 1; i < list.length; i++) {
    if (list[i].collectionStatus === null) {
      candidate = list[i - 1];
      break;
    }
  }

  if (!candidate) {
    candidate = [...list].reverse().find((item) => item.collectionStatus === "PAID");
  }

  if (
    candidate &&
    candidate.paymentType === "PREPAYMENT" &&
    Number(candidate.collectionAmount) === 0
  ) {
    const idx = list.findIndex(
      (item) => item.savingsCollectionId === candidate.savingsCollectionId,
    );

    for (let j = idx - 1; j >= 0; j--) {
      const row = list[j];
      if (row.collectionStatus !== "PAID" || row.paymentType !== "PREPAYMENT") break;
      if (Number(row.collectionAmount) > 0) {
        candidate = row;
        break;
      }
    }
  }

  return candidate;
};

  const openRevertDialog = (row) => {
    setRowToRevert(row);
    setRevertDialogOpen(true);
  };

  const closeRevertDialog = () => {
    if (reverting) return;
    setRevertDialogOpen(false);
    setRowToRevert(null);
  };

  const handleConfirmRevert = async () => {
    if (!rowToRevert) return;

    setReverting(true);
    try {
      const resp = await axios.put(
        `${BaseUrl}/savings/revertSavingCollection`,
        {
          savingsCollectionId: rowToRevert.savingsCollectionId,
          savingId,
        },
        { headers: authHeaders() },
      );

      if (resp.data?.status) {
        toast.success(resp.data.msg || "Saving Reverted Successfully!", TOAST_OPTS);
        closeRevertDialog();
        fetchSaving();
      } else {
        toast.error(resp.data?.msg || "Please try again later!", TOAST_OPTS);
      }
    } catch (err) {
      console.error("revertSavingCollection Error:", err);
      toast.error(
        err?.response?.data?.msg || "Please try again later!",
        TOAST_OPTS,
      );
    } finally {
      setReverting(false);
    }
  };

  const fetchSaving = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(
        `${BaseUrl}/savings/getSavingById/${savingId}`,
        {
          headers: authHeaders(),
        },
      );

      const data = resp.data?.data || resp.data;

      setSaving(data.saving || data);
      setSavingsCollections(
        data.savingsCollections || data.saving?.savingsCollections || [],
      );
    } catch (err) {
      console.error("GetSavingById Error:", err);
      toast.error("Unable to fetch savings details!", TOAST_OPTS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSaving();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savingId]);

  // A refetch (or navigating to another saving) must not leave stale pending
  // edits sitting on top of freshly loaded rows.
  useEffect(() => {
    setIsEditing(false);
    setChanges({});
    setWarnedFields({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savingId]);

  const derived = useMemo(() => {
    if (!saving || savingsCollections.length === 0) return null;

    const sortedCollections = [...savingsCollections].sort(
      (a, b) => new Date(a.dueDate) - new Date(b.dueDate),
    );

    const nextCollection =
      sortedCollections.find((c) => isPendingStatus(c.collectionStatus)) || null;

    const firstDate = sortedCollections[0]?.dueDate;
    const lastDate = sortedCollections[sortedCollections.length - 1]?.dueDate;

    const totalAmount = Number(saving.totalAmount || 0);
    const totalInterest = Number(saving.totalInterest || 0);
    const savingMethod = saving.savingMethod;
    const preClosedAmt = saving.preClosedAmt ?? null;

    const amountPaid = sortedCollections
      .filter((c) => isPaidStatus(c.collectionStatus))
      .reduce((sum, c) => sum + Number(c.collectionAmount || 0), 0);

    const interestPaid = sortedCollections
      .filter((c) => isPaidStatus(c.collectionStatus))
      .reduce((sum, c) => sum + Number(c.interest || 0), 0);

    const totalMaturityAmount = savingMethod === 'recurring' ?
    sortedCollections[sortedCollections?.length - 1] :
    totalAmount+totalInterest;

    const totalBalanceAmount = preClosedAmt ? 0 : Math.max(totalAmount - amountPaid, 0);

    const percentReceived =
      totalAmount > 0 ? Math.round((amountPaid / totalAmount) * 100) : 0;

    return {
      sortedCollections,
      nextCollection,
      durationLabel: `${formatDate(firstDate)}-${formatDate(lastDate)}`,
      amountPaid,
      interestPaid,
      totalAmount,
      totalInterest,
      totalMaturityAmount: totalMaturityAmount?.maturityAmount ?? totalMaturityAmount,
      totalBalanceAmount,
      percentReceived,
      savingMethod,
      preClosedAmt
    };
  }, [saving, savingsCollections]);

  const handleBack = () => history.goBack();
  const handleTransactionHistory = () =>
    toast.info(
      "Transaction History — wire this up to your ledger view.",
      TOAST_OPTS,
    );

  // ---------------- Schedule edit handlers ----------------

  const startEditing = () => {
    setChanges({});
    setWarnedFields({});
    setIsEditing(true);
  };

  const cancelEditing = () => {
    if (savingEdits) return;
    setIsEditing(false);
    setChanges({});
    setWarnedFields({});
  };

  /** Current value for a cell: pending edit first, then the persisted row. */
  const cellValue = (row, field) => {
    const pending = changes[row.savingsCollectionId];
    if (pending && Object.prototype.hasOwnProperty.call(pending, field)) {
      return pending[field];
    }
    if (DATE_FIELDS.includes(field)) return toInputDate(row[field]);
    if (field === "collectionStatus") return normalizeStatus(row[field]);
    return row[field] ?? "";
  };

  const handleFieldChange = (row, field, value) => {
    const id = row.savingsCollectionId;

    setChanges((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));

    // Warnings mirror the loan schedule editor: status warns every time it
    // flips, the rest warn once per edit session so they don't nag.
    if (field === "collectionStatus") {
      if (normalizeStatus(value) === normalizeStatus(row.collectionStatus)) return;
      if (normalizeStatus(value) === "NOT_PAID") {
        setWarnMessage(
          "If you mark this as Not Paid, reset the collection amount to 0 and adjust the opening & closing balances accordingly.",
        );
      } else if (normalizeStatus(value) === "PAID") {
        setWarnMessage("If you mark this as Paid, enter the collection amount actually received.");
      }
      return;
    }

    if (isSameValue(field, row[field], value)) return;
    if (FIELD_WARNINGS[field] && !warnedFields[field]) {
      setWarnedFields((prev) => ({ ...prev, [field]: true }));
      setWarnMessage(FIELD_WARNINGS[field]);
    }
  };

  /** Build the PUT body: only rows, only columns, that actually differ. */
  const buildEditPayload = () =>
    Object.entries(changes)
      .map(([id, fields]) => {
        const original =
          savingsCollections.find(
            (r) => String(r.savingsCollectionId) === String(id),
          ) || {};
        const dirty = {};

        Object.entries(fields).forEach(([field, value]) => {
          const before = DATE_FIELDS.includes(field)
            ? toInputDate(original[field])
            : field === "collectionStatus"
              ? normalizeStatus(original[field])
              : original[field] ?? "";

          if (isSameValue(field, before, value)) return;

          dirty[field] =
            NUMERIC_FIELDS.includes(field) && value !== "" && value !== null
              ? Number(value)
              : value;
        });

        return Object.keys(dirty).length > 0
          ? { savingsCollectionId: original.savingsCollectionId ?? id, ...dirty }
          : null;
      })
      .filter(Boolean);

  const handleSaveEdits = async () => {
    const payload = buildEditPayload();

    if (payload.length === 0) {
      toast.info("No changes to save.", TOAST_OPTS);
      cancelEditing();
      return;
    }

    const invalid = payload.find((row) =>
      Object.entries(row).some(
        ([field, value]) =>
          NUMERIC_FIELDS.includes(field) &&
          value !== null &&
          value !== "" &&
          (!Number.isFinite(Number(value)) || Number(value) < 0),
      ),
    );

    if (invalid) {
      toast.error("Amounts must be valid, non-negative numbers.", TOAST_OPTS);
      return;
    }

    try {
      setSavingEdits(true);

      const resp = await axios.put(
        `${BaseUrl}/savings/editSavingCollection`,
        payload,
        { headers: authHeaders() },
      );

      // The endpoint answers { status, msg, message }; older savings endpoints
      // only send `msg`, so read both and don't insist on `status` being true.
      if (resp.data?.status === false) {
        toast.error(resp.data?.msg || "Please try again later!", TOAST_OPTS);
        return;
      }

      toast.success(
        resp.data?.msg || resp.data?.message || "Savings schedule updated successfully",
        TOAST_OPTS,
      );

      setIsEditing(false);
      setChanges({});
      setWarnedFields({});
      fetchSaving();
    } catch (err) {
      console.error("editSavingCollection Error:", err);
      toast.error(
        err.response?.data?.msg ||
          err.response?.data?.message ||
          "Please try again later!",
        TOAST_OPTS,
      );
    } finally {
      setSavingEdits(false);
    }
  };

  const openModal = (action) => {
    setModalAction(action);
    setPaymentMode("Cash");
    setPaymentType("FULL");
    setCollectedDate(todayISODate());
    setCollectionAmount(
      derived?.nextCollection ? derived.nextCollection.collectionAmount : 0,
    );
    setPenaltyAmount(0);
    setAmountError("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (submitting) return;
    setModalOpen(false);
  };

  const handlePaymentTypeChange = (value) => {
    setPaymentType(value);
    if (value === "FULL") {
      setCollectionAmount(
        derived?.nextCollection ? derived.nextCollection.collectionAmount : 0,
      );
      setAmountError("");
    }
  };

  const handleAmountChange = (e) => {
    const raw = e.target.value;
    setCollectionAmount(raw === "" ? "" : Number(raw));

    const scheduled = derived?.nextCollection?.collectionAmount ?? 0;
    if (raw === "" || Number(raw) <= 0) {
      setAmountError("Enter a valid amount");
    } else if (paymentType === "PARTIAL" && Number(raw) > scheduled) {
      setAmountError("Partial amount can't exceed the scheduled amount");
    } else {
      setAmountError("");
    }
  };

  const handlePenaltyChange = (e) => {
    const raw = e.target.value;
    setPenaltyAmount(raw === "" ? "" : Number(raw));
  };

  const handleConfirm = async () => {
    if (!derived?.nextCollection) return;

    if (collectionAmount === "" || Number(collectionAmount) <= 0) {
      setAmountError("Enter a valid amount");
      return;
    }

    const loggedinUser = getSecureItem("loggedinUser");

    try {
      setSubmitting(true);
      const resp = await axios.put(
        `${BaseUrl}/savings/putSavingCollection`,
        {
          savingsCollectionId: derived.nextCollection.savingsCollectionId,
          collectionStatus: modalAction,
          collectedDate,
          collectorId: loggedinUser?.userId,
          paymentMode: paymentMode,
          paymentType: paymentType,
          collectionAmount: Number(collectionAmount),
          penaltyApplied: penaltyAmount === "" ? 0 : Number(penaltyAmount),
          companyId: saving?.companyId,
          mobNo: saving?.customer?.phone,
          name: saving?.customer?.firstName + " " + saving?.customer?.lastName,
          companyName: loggedinUser?.company?.companyName || loggedinUser?.firstName + " " + loggedinUser?.lastName,
        },
        { headers: authHeaders() },
      );

      if (resp.data?.status) {
        toast.success(resp.data.msg || "Updated successfully", TOAST_OPTS);
        setModalOpen(false);
        fetchSaving();
      } else {
        toast.error(resp.data?.msg || "Please try again later!", TOAST_OPTS);
      }
    } catch (err) {
      console.error("putSavingCollection Error:", err);
      toast.error(
        err.response?.data?.msg || "Please try again later!",
        TOAST_OPTS,
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Cell editors, kept tiny so the table below stays readable ----
  const editNumber = (row, field) => (
    <TextField
      type="number"
      size="small"
      variant="outlined"
      className={classes.editInput}
      value={cellValue(row, field)}
      onChange={(e) => handleFieldChange(row, field, e.target.value)}
      inputProps={{ min: 0, step: "any" }}
    />
  );

  const editDate = (row, field) => (
    <TextField
      type="date"
      size="small"
      variant="outlined"
      className={classes.editInput}
      value={cellValue(row, field)}
      onChange={(e) => handleFieldChange(row, field, e.target.value)}
      InputLabelProps={{ shrink: true }}
    />
  );

  const editStatus = (row) => (
    <TextField
      select
      size="small"
      variant="outlined"
      className={classes.editInput}
      value={cellValue(row, "collectionStatus")}
      onChange={(e) => handleFieldChange(row, "collectionStatus", e.target.value)}
    >
      {STATUS_OPTIONS.map((option) => (
        <MenuItem key={option.value || "pending"} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );

  if (loading) {
    return (
      <div className={classes.loaderWrapper}>
        <CircularProgress />
      </div>
    );
  }

  if (!saving || !derived) {
    return (
      <div className={classes.loaderWrapper}>
        <Typography>Savings not found.</Typography>
      </div>
    );
  }

  const {
    sortedCollections,
    nextCollection,
    durationLabel,
    amountPaid,
    interestPaid,
    totalMaturityAmount,
    totalBalanceAmount,
    percentReceived,
    savingMethod,
    preClosedAmt,
  } = derived;

  const hasPendingCollection = Boolean(nextCollection);
  const canEditNow = Boolean(canEditSchedule) && isSavingEditable(saving);
  const dirtyRowCount = buildEditPayload().length;
  const revertableData = getRevertableCollection(sortedCollections);

  // Penalty is relevant whenever the missed/short amount is being carried
  // forward — i.e. Not Paid, or a Partial payment. A Full payment settles the
  // installment exactly, so its amount field is fixed and not editable.
  const showPenaltyField = modalAction === "NOT_PAID" || paymentType === "PARTIAL";
  const isAmountDisabled = paymentType === "FULL";

  const transactionHistory = [...sortedCollections]
    .filter((row) => !isPendingStatus(row.collectionStatus))
    .sort(
      (a, b) =>
        new Date(b.collectedDate || b.dueDate) -
        new Date(a.collectedDate || a.dueDate),
    );

  return (
    <>
    <Grid container className={classes.page}>
      {/* ---------------- LEFT SIDEBAR ---------------- */}
      <Grid item xs={12} md={3} className={classes.sidebar}>
        <div className={classes.sidebarTopRow}>
          <IconButton
            className={classes.backIcon}
            onClick={handleBack}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
          {saving.savingStatus === "Open" && (
            <button
              style={{
                cursor: 'pointer',
                backgroundColor: '#00d95e',
                fontSize: '12px',
                color: '#fff',
                fontWeight: 500,
                padding: '6px 12px',
                borderRadius: '5px',
                border: 'none',
              }}
              onClick={(e) => openCancelDialog(e, saving)}
            >
              Cancel
            </button>
          )}
        </div>

        <div className={classes.avatarWrapper}>
          {saving?.customer?.proofs[0]?.proofType == "profilepic" ? (
            <img
              width={"80px"}
              height={"80px"}
              style={{ borderRadius: "50%" }}
              alt="Logo"
              src={ImageBaseUrl + saving?.customer?.proofs[0]?.proofImagePath}
            />
          ) : (
            <AccountCircleIcon className={classes.avatarIcon} />
          )}
        </div>

        <Typography className={`Fonts ${classes.customerName}`}>
          {saving.customer
            ? `${saving.customer.firstName} ${saving.customer.lastName} (${saving.customer.cust_id})`
            : "-"}
        </Typography>
        <div align="center">
          <Typography
            style={{
              width: "90px",
              marginBottom: 10,
              textAlign: "center",
              background: "#fff",
              color: "#000",
              padding: "5px 20px",
              borderRadius: 5,
            }}
            className="Fonts"
          >
            {saving.savingCode}
          </Typography>
        </div>
        <div className={classes.phoneRow}>
          <Phone fontSize="small" />
          <Typography className="Fonts">{saving.customer.phone}</Typography>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 5,
          }}
        >
          <div>
            <Typography className={`Fonts ${classes.nextPaymentLabel}`}>
              NEXT PAYMENT
            </Typography>
            <Typography className={`Fonts ${classes.nextPaymentDate}`}>
              {hasPendingCollection ? formatDate(nextCollection.dueDate) : "-"}
            </Typography>
            <Typography className={`Fonts ${classes.nextPaymentAmount}`}>
              {hasPendingCollection ? nextCollection.collectionAmount : 0}
            </Typography>
            <Typography className={`Fonts ${classes.nextPaymentSub}`}>
              {hasPendingCollection ? (nextCollection.principal ?? 0) : 0}{" "}
              Principal &nbsp;|&nbsp;{" "}
              {hasPendingCollection ? (nextCollection.penaltyApplied ?? 0) : 0}{" "}
              Penalty
            </Typography>
          </div>
          <div className={classes.actionButtonsRow}>
            <Button
              className={classes.receiveButton}
              onClick={() => openModal("PAID")}
              disabled={!hasPendingCollection || isEditing}
            >
              Receive Payment
            </Button>
            <Button
              className={classes.notPaidButton}
              onClick={() => openModal("NOT_PAID")}
              disabled={!hasPendingCollection || isEditing}
            >
              Not Paid
            </Button>
          </div>
        </div>

        <Typography
          className={`Fonts ${classes.transactionHistory}`}
          onClick={handleTransactionHistory}
        >
          Transaction History
        </Typography>

        <div className={classes.transactionHistoryContainer}>
          {transactionHistory?.reverse()?.map((row, idx) => {
            const transactionDate = row.collectedDate || row.dueDate;
            const date = new Date(transactionDate);
            const month = date.toLocaleString("en-US", { month: "short" });
            const day = date.getDate();
            const isPaid = isPaidStatus(row.collectionStatus);
            const statusColor = isPaid ? COLOR_PAID : COLOR_NOT_PAID;

            return (
              <div
                key={row.savingsCollectionId || idx}
                className={classes.transactionRow}
              >
                <div className={classes.dateBox}>
                  <Typography className={`Fonts ${classes.dateMonth}`}>
                    {month}
                  </Typography>
                  <Typography className={`Fonts ${classes.dateDay}`}>
                    {day}
                  </Typography>
                </div>

                <div className={classes.amountSection}>
                  <Typography className={`Fonts ${classes.amount}`}>
                    {row.collectionAmount}
                  </Typography>
                  <Typography className={`Fonts ${classes.installment}`}>
                    {row.collectionAmount} Installment
                  </Typography>
                </div>

                <div className={classes.statusSection}>
                  <div className={classes.statusRow}>
                    <span
                      className={classes.statusDot}
                      style={{ backgroundColor: statusColor }}
                    />
                    <Typography
                      className={`Fonts ${classes.statusText}`}
                      style={{ color: statusColor }}
                    >
                      {isPaid ? "FULL" : "NOT PAID"}
                    </Typography>
                  </div>

                  {isPaid && (
                    <div className={classes.paymentRow}>
                      <Typography className={`Fonts ${classes.paymentMode}`}>
                        {row.paymentMode || "Cash"}
                      </Typography>
                      <span className={classes.checkIcon}>✓</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Grid>

      {/* ---------------- RIGHT MAIN PANEL ---------------- */}
      <Grid item xs={12} md={9} className={classes.main}>
        <div style={{marginBottom: 10}}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography className={`Fonts ${classes.infoValue}`}>
                {savingMethod === 'recurring' ? 'Recurring Deposit' : 'Fixed Deposit (Simple Interest)'}
              </Typography>
              <Typography className={`Fonts ${classes.infoLabel}`}>
                Method
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}></Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Button
                className={classes.receiveButton}
                onClick={() => setShowPreClose(!showPreClose)}
                disabled={!hasPendingCollection || isEditing}
              >
                PreClose Saving
              </Button>
            </Grid>
          </Grid>
        </div>
        <div className={classes.infoBar}>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={4} md={4}>
              <Typography className={`Fonts ${classes.infoValue}`}>
                {durationLabel}
              </Typography>
              <Typography className={`Fonts ${classes.infoLabel}`}>
                Duration
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Typography className={`Fonts ${classes.infoValue}`}>
                {capitalize(saving.tenureType)}
              </Typography>
              <Typography className={`Fonts ${classes.infoLabel}`}>
                Collection Sequence
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Typography className={`Fonts ${classes.infoValue}`}>
                {saving.user
                  ? `${saving.user.firstName} ${saving.user.lastName}`
                  : "-"}
              </Typography>
              <Typography className={`Fonts ${classes.infoLabel}`}>
                Collection Agent
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Typography className={`Fonts ${classes.infoValue}`}>
                {saving.rateOfInterest}%
              </Typography>
              <Typography className={`Fonts ${classes.infoLabel}`}>
                Rate of Interest
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Typography className={`Fonts ${classes.infoValue}`}>
                {saving.tenure} {TENURE_LABELS[saving?.tenureType] ?? 'Months'}
              </Typography>
              <Typography className={`Fonts ${classes.infoLabel}`}>
                Tenure
              </Typography>
            </Grid>
          </Grid>
        </div>

        <Grid
          container
          spacing={3}
          className={classes.statsRow}
          alignItems="center"
        >
          <Grid item xs={12} sm={6} md={3}>
            <div className={classes.percentRingWrapper}>
              <PercentRing percent={percentReceived} />
              <Typography className={`Fonts ${classes.percentLabel}`}>
                Received
              </Typography>
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <div className={classes.statBox}>
              <Typography className={`Fonts ${classes.statValue}`}>
                {currency} {amountPaid}
              </Typography>
              <Typography className={`Fonts ${classes.statLabel}`}>
                Amount Paid
              </Typography>
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <div className={classes.statBox}>
              <Typography className={`Fonts ${classes.statValue}`}>
                {currency} {totalBalanceAmount}
              </Typography>
              <Typography className={`Fonts ${classes.statLabel}`}>
                Balance Amount
              </Typography>
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <div className={classes.statBox}>
              <Typography className={`Fonts ${classes.statValue}`}>
                {currency} {preClosedAmt ? preClosedAmt : totalMaturityAmount}
              </Typography>
              <Typography className={`Fonts ${classes.statLabel}`}>
                Maturity Amount
              </Typography>
            </div>
          </Grid>
        </Grid>

        <div className={classes.scheduleHeaderRow}>
          <Typography className={`Fonts ${classes.scheduleTitle}`}>
            Savings Schedule
          </Typography>
          <Typography className={`Fonts ${classes.scheduleCount}`}>
            {sortedCollections.length} {saving.tenureType?.toLowerCase()}
          </Typography>

          {canEditSchedule && (
            <div className={classes.scheduleActions}>
              {isEditing ? (
                <>
                  <Typography className={`Fonts ${classes.scheduleCount}`}>
                    {dirtyRowCount > 0
                      ? `${dirtyRowCount} row${dirtyRowCount > 1 ? "s" : ""} changed`
                      : "No changes yet"}
                  </Typography>
                  <Button
                    className={classes.editSaveButton}
                    onClick={handleSaveEdits}
                    disabled={savingEdits}
                  >
                    {savingEdits ? (
                      <CircularProgress size={16} style={{ color: "#fff" }} />
                    ) : (
                      "Save"
                    )}
                  </Button>
                  <Button
                    className={classes.editCancelButton}
                    onClick={cancelEditing}
                    disabled={savingEdits}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Tooltip
                  title={
                    canEditNow
                      ? "Edit Savings Schedule"
                      : "This saving is closed — schedule can't be edited"
                  }
                >
                  {/* span keeps the tooltip working while the button is disabled */}
                  <span>
                    <IconButton
                      aria-label="edit savings schedule"
                      size="medium"
                      className={classes.editIcon}
                      onClick={startEditing}
                      disabled={!canEditNow}
                    >
                      <EditOutlinedIcon fontSize="inherit" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </div>
          )}
        </div>

        <TableContainer component={Paper} className={classes.tableWrapper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={`Fonts ${classes.tableHeadCell}`}>
                  #
                </TableCell>
                <TableCell className={`Fonts ${classes.tableHeadCell}`}>
                  Due Date
                </TableCell>
                <TableCell className={`Fonts ${classes.tableHeadCell}`}>
                  Opening Balance
                </TableCell>
                <TableCell className={`Fonts ${classes.tableHeadCell}`}>
                  Collection Amt
                </TableCell>
                <TableCell className={`Fonts ${classes.tableHeadCell}`}>
                  Closing Balance
                </TableCell>
                {savingMethod === "recurring" && (
                  <TableCell className={`Fonts ${classes.tableHeadCell}`}>
                    Interest Applied
                  </TableCell>
                )}
                {savingMethod === "recurring" && (
                  <TableCell className={`Fonts ${classes.tableHeadCell}`}>
                    Maturity Amount
                  </TableCell>
                )}
                <TableCell className={`Fonts ${classes.tableHeadCell}`}>
                  Penalty
                </TableCell>
                <TableCell className={`Fonts ${classes.tableHeadCell}`}>
                  Collection Status
                </TableCell>
                <TableCell className={`Fonts ${classes.tableHeadCell}`}>
                  Collected Date
                </TableCell>
                <TableCell className={`Fonts ${classes.tableHeadCell}`}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedCollections.map((row, idx) => (
                <TableRow key={row.savingsCollectionId || idx}>
                  <TableCell className="Fonts">{idx + 1}</TableCell>
                  <TableCell className="Fonts">
                    {isEditing
                      ? editDate(row, "dueDate")
                      : formatDDMMYYYY(row.dueDate)}
                  </TableCell>
                  <TableCell className="Fonts">
                    {isEditing
                      ? editNumber(row, "openingBalance")
                      : row.openingBalance}
                  </TableCell>
                  <TableCell
                    className="Fonts"
                    style={{
                      color: isPaidStatus(row.collectionStatus)
                        ? COLOR_PAID
                        : isNotPaidStatus(row.collectionStatus)
                          ? COLOR_NOT_PAID
                          : "#4d66f0",
                      fontWeight: 600,
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 5
                    }}
                  >
                    {isEditing ? (
                      editNumber(row, "collectionAmount")
                    ) : (
                      <>
                        <span>{row.collectionAmount}</span>
                        {isPaidStatus(row.collectionStatus) ? (
                          <span className={classes.checkIcon}>✓</span>
                        ) : isNotPaidStatus(row.collectionStatus) ? (
                          <span className={classes.crossIcon}>x</span>
                        ) : null}
                      </>
                    )}
                  </TableCell>
                  <TableCell className="Fonts">
                    {isEditing
                      ? editNumber(row, "closingBalance")
                      : row.closingBalance}
                  </TableCell>
                  {savingMethod === "recurring" && (
                    <TableCell className="Fonts">
                      {isEditing
                        ? editNumber(row, "interest")
                        : row.interest || "-"}
                    </TableCell>
                  )}
                  {savingMethod === "recurring" && (
                    <TableCell className="Fonts">
                      {isEditing
                        ? editNumber(row, "maturityAmount")
                        : row.maturityAmount || "-"}
                    </TableCell>
                  )}
                  <TableCell className="Fonts">
                    {isEditing
                      ? editNumber(row, "penaltyApplied")
                      : row.penaltyApplied || "-"}
                  </TableCell>
                  <TableCell className="Fonts">
                    {isEditing
                      ? editStatus(row)
                      : isPaidStatus(row.collectionStatus)
                        ? "Paid"
                        : isNotPaidStatus(row.collectionStatus)
                          ? "Not Paid"
                          : "-"}
                  </TableCell>
                  <TableCell className="Fonts">
                    {isEditing
                      ? editDate(row, "collectedDate")
                      : row.collectedDate
                        ? formatDDMMYYYY(row.collectedDate)
                        : "-"}
                  </TableCell>
                  <TableCell className="Fonts">
                    {userPermissions?.revertPayment &&
                      revertableData?.savingsCollectionId === row.savingsCollectionId && (
                        <IconButton
                          size="small"
                          title="Revert"
                          style={{ display: saving.savingStatus !== "Open" ? "none" : "inline-flex" }}
                          onClick={() => openRevertDialog(row)}
                        >
                          <Undo fontSize="small" style={{
                      color:"#4d66f0"}}/>
                        </IconButton>
                      )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      {/* ---------------- RECEIVE PAYMENT / NOT PAID MODAL ---------------- */}
      <Dialog
        open={modalOpen}
        onClose={closeModal}
        classes={{ paper: classes.modalPaper }}
        fullWidth
      >
        <div className={classes.modalHeaderRow}>
          <Typography className={`Fonts ${classes.modalDueDate}`}>
            {hasPendingCollection ? formatDate(nextCollection.dueDate) : "-"}
          </Typography>
          <Typography
            className={`Fonts ${
              modalAction === "PAID"
                ? classes.modalTitlePaid
                : classes.modalTitleNotPaid
            }`}
          >
            {modalAction === "PAID" ? "Receive Payment" : "Mark as Not Paid"}
          </Typography>
          <IconButton size="small" onClick={closeModal}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
          {modalAction === "PAID" && (<Grid item xs={12} sm={12} style={{
            display: "flex",
            flexDirection: "column",
            marginLeft: "20px",
            font:"bold",
          }}>
            <FormControl component="fieldset">
              <RadioGroup
                row
                value={paymentType}
                onChange={(e) => {
                  handlePaymentTypeChange(e.target.value);
                }}
              >
                <FormControlLabel
                  value="FULL"
                  control={
                    <Radio
                      size="small"
                      classes={{ root: classes.radioRoot, checked: classes.radioChecked }}
                    />
                  }
                  label="Full"
                />
                <FormControlLabel
                  value="PARTIAL"
                  control={
                    <Radio
                      size="small"
                      classes={{ root: classes.radioRoot, checked: classes.radioChecked }}
                    />
                  }
                  label="Partial"
                />
                <FormControlLabel
                  value="PREPAYMENT"
                  control={
                    <Radio
                      size="small"
                      classes={{ root: classes.radioRoot, checked: classes.radioChecked }}
                    />
                  }
                  label="Prepayment"
                />
              </RadioGroup>
            </FormControl>
          </Grid>)}

        <DialogContent>
          <Grid container spacing={3} className={classes.modalFieldsRow}>
            <Grid item xs={12} sm={showPenaltyField ? 3 : 4}>
              <Typography className={`Fonts ${classes.modalFieldLabel}`}>
                Amount
              </Typography>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                value={collectionAmount}
                onChange={handleAmountChange}
                error={Boolean(amountError)}
                helperText={amountError || ""}
                inputProps={{ min: 0 }}
                disabled={isAmountDisabled}
              />
            </Grid>
            {showPenaltyField && (
              <Grid item xs={12} sm={3}>
                <Typography className={`Fonts ${classes.modalFieldLabel}`}>
                  Penalty
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  variant="outlined"
                  value={penaltyAmount}
                  onChange={handlePenaltyChange}
                  inputProps={{ min: 0 }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={showPenaltyField ? 3 : 4}>
              <Typography className={`Fonts ${classes.modalFieldLabel}`}>
                Payment Mode
              </Typography>
              <TextField
                select
                fullWidth
                variant="outlined"
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
              >
                {PAYMENT_MODES.map((mode) => (
                  <MenuItem key={mode} value={mode}>
                    {mode}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={showPenaltyField ? 3 : 4}>
              <Typography className={`Fonts ${classes.modalFieldLabel}`}>
                {modalAction === "PAID" ? "Collected Date" : "Missed Date"}
              </Typography>
              <TextField
                type="date"
                fullWidth
                variant="outlined"
                value={collectedDate}
                onChange={(e) => setCollectedDate(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <div className={classes.modalFooter}>
          <div>
            <Typography className={`Fonts ${classes.modalTotalAmount}`}>
              INR {collectionAmount === "" ? 0 : collectionAmount}
            </Typography>
            <Typography className={`Fonts ${classes.modalTotalLabel}`}>
              Total
            </Typography>
          </div>
          <div>
            <Button
              className={classes.modalCancelButton}
              onClick={closeModal}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              className={classes.modalConfirmButtonPaid}
              onClick={handleConfirm}
              disabled={submitting || Boolean(amountError)}
            >
              {submitting ? (
                <CircularProgress size={20} style={{ color: "#fff" }} />
              ) : (
                "Confirm"
              )}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* --------------- Cancel Saving Dialog --------------- */}
      <Dialog
        open={cancelDialogOpen}
        onClose={closeCancelDialog}
        maxWidth="xs"
        fullWidth
        className={classes.root}
      >
        <DialogTitle className="Fonts">Cancel Saving</DialogTitle>
        <DialogContent>
          <DialogContentText className="Fonts">
            {savingToCancel?.customer
              ? `Cancelling saving for ${savingToCancel.customer.firstName} ${savingToCancel.customer.lastName}. Please provide a reason.`
              : "Please provide a reason for cancelling this saving."}
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            variant="outlined"
            placeholder="Enter reason for cancellation..."
            value={cancelReason}
            onChange={(e) => {
              setCancelReason(e.target.value);
              if (e.target.value.trim()) setReasonError(false);
            }}
            error={reasonError}
            helperText={reasonError ? "Reason is required" : ""}
            disabled={cancelling}
            style={{ marginTop: 8, position: "initial" }}
          />
        </DialogContent>
        <DialogActions style={{ padding: "8px 24px 20px" }}>
          <Button onClick={closeCancelDialog} disabled={cancelling} style={{
              textTransform: 'capitalize',
              backgroundColor: '#ddd',
              padding: '5px 15px',
              borderRadius: '6px',
            }}>
            Back
          </Button>
          <Button
            onClick={handleConfirmCancel}
            disabled={cancelling}
            style={{
              marginLeft: '1rem',
              backgroundColor: '#00d95e',
              color: 'white',
              textTransform: 'capitalize',
              padding: '5px 15px',
              borderRadius: '6px',
            }}
          >
            {cancelling ? "Cancelling..." : "Cancel"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={revertDialogOpen} style={{ borderRadius: "5px" }}>
        <DialogTitle>
          <Typography className="Fonts" style={{ fontSize: 15 }}>
            All changes for this collection will be reverted. Do you want to proceed?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <div align="right">
            <button
              style={{
                cursor: "pointer",
                padding: 10,
                fontSize: "15px",
                background: "white",
                border: "1px solid lightgray",
                borderRadius: "5px",
                fontFamily: "Lato",
                color: "#000",
              }}
              onClick={closeRevertDialog}
              disabled={reverting}
            >
              Cancel
            </button>
            <button
              style={{
                cursor: "pointer",
                padding: 10,
                fontSize: "15px",
                background: "#00d95e",
                color: "#fff",
                border: "none",
                borderRadius: "5px",
                fontFamily: "Lato",
                marginLeft: 10,
              }}
              onClick={handleConfirmRevert}
              disabled={reverting}
            >
              {reverting ? "Reverting..." : "Revert"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </Grid>
    {/* --------------- Schedule edit warning --------------- */}
    <Dialog
      open={Boolean(warnMessage)}
      onClose={() => setWarnMessage("")}
      maxWidth="xs"
    >
      <DialogContent>
        <DialogContentText
          className="Fonts"
          style={{ fontSize: 15, color: "#000", marginBottom: 4 }}
        >
          {warnMessage}
        </DialogContentText>
      </DialogContent>
      <DialogActions style={{ padding: "0 16px 16px", justifyContent: "center" }}>
        <Button
          className={classes.editSaveButton}
          onClick={() => setWarnMessage("")}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
    <PreCloseSaving open={showPreClose} close={() => setShowPreClose(false)} savingsCollectionId={nextCollection?.savingsCollectionId} savingId={savingId} balanceAmt={amountPaid} interestAmt={interestPaid} />
    </>
  );
};

export default SavingDetail;

